(function () {
  const SECTION_TYPE = 'cart-page';
  const CART_ICON_SECTION_ID = 'cart-icon-bubble';
  const FREE_SHIPPING_STATE_SELECTOR = '[data-cart-free-shipping-state]';
  const CART_STATE_EVENT_KEY_EMPTY = 'cart_emptied';
  const CLARITY_READY_RETRY_MS = 100;
  const CLARITY_READY_MAX_ATTEMPTS = 30;
  const CROSS_SELL_VIEW_THRESHOLD = 0.5;
  const CROSS_SELL_VIEW_DWELL_MS = 400;
  let cartCrossSellGroups = null;
  let cartCrossSellOverlayState = null;
  let cartCrossSellScrollY = 0;
  let cartCrossSellScrollLocked = false;
  let pendingCartAction = null;
  let lastFreeShippingState = null;
  let cartOpenTracked = false;
  let crossSellViewedThisPage = false;
  let crossSellImpressedThisPage = false;
  let crossSellViewObserver = null;
  let crossSellViewTimer = null;

  function getCartPage(doc = document) {
    return doc.querySelector('.cart-page');
  }

  function getCartPageSectionId() {
    const cartPage = getCartPage();
    return cartPage && cartPage.dataset.sectionId ? cartPage.dataset.sectionId : SECTION_TYPE;
  }

  function getSectionRoot(doc = document) {
    const cartPage = getCartPage(doc);
    if (cartPage) return cartPage.closest('.shopify-section') || cartPage;

    return doc.getElementById('shopify-section-' + getCartPageSectionId());
  }

  function getSectionInnerHTML(html, selector = '.shopify-section') {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const selected = doc.querySelector(selector);
    return selected ? selected.innerHTML : html;
  }

  function setLoading(isLoading) {
    const root = getSectionRoot();
    if (root) root.classList.toggle('cart-page__loading', isLoading);
  }

  function updateCartIconCount(itemCount) {
    if (typeof itemCount !== 'number') return;

    const countBubble = document.querySelector('.cart-count-bubble');
    if (!countBubble) return;

    const visibleCount = countBubble.querySelector('span[aria-hidden="true"]');
    const screenReaderCount = countBubble.querySelector('span.visually-hidden');

    if (visibleCount) visibleCount.textContent = itemCount;
    if (screenReaderCount) screenReaderCount.textContent = `${itemCount} item${itemCount === 1 ? '' : 's'}`;
  }

  function getCartStateRoot() {
    return document.querySelector(FREE_SHIPPING_STATE_SELECTOR);
  }

  function getCartFreeShippingEventName() {
    const stateEl = getCartStateRoot();
    if (!stateEl) return null;

    if (stateEl.dataset.itemCount === '0') return CART_STATE_EVENT_KEY_EMPTY;
    if (stateEl.dataset.freeShippingMode === 'hidden') return null;
    if (stateEl.dataset.freeShippingMode === 'message' && stateEl.dataset.hasHeavyBulky !== 'true') return 'cart_free_shipping_no_minimum';
    if (stateEl.dataset.hasHeavyBulky === 'true') return 'cart_free_shipping_blocked_heavy_bulky';

    return stateEl.dataset.freeShippingUnlocked === 'true'
      ? 'cart_free_shipping_threshold_met'
      : 'cart_free_shipping_threshold_not_met';
  }

  function getCartFreeShippingState() {
    const stateEl = getCartStateRoot();
    if (!stateEl) return 'not_met';
    if (stateEl.dataset.freeShippingMode === 'hidden') return 'hidden';
    if (stateEl.dataset.freeShippingMode === 'message' && stateEl.dataset.hasHeavyBulky !== 'true') return 'no_minimum';
    if (stateEl.dataset.hasHeavyBulky === 'true') return 'blocked';

    return stateEl.dataset.freeShippingUnlocked === 'true' ? 'met' : 'not_met';
  }

  function getCartValueBand(totalCents) {
    const totalDollars = Math.max(0, Math.floor(Number(totalCents || 0) / 100));
    if (totalDollars >= 400) return '400_plus';

    const lower = Math.floor(totalDollars / 50) * 50;
    const upper = lower + 49;

    return `${lower}_${upper}`;
  }

  function getCartStateTags() {
    const stateEl = getCartStateRoot();
    if (!stateEl) return null;

    const totalCents = Number(stateEl.dataset.cartTotal || 0);

    return {
      surface: 'page',
      freeShippingState: getCartFreeShippingState(),
      valueBand: getCartValueBand(totalCents),
      itemCount: Number(stateEl.dataset.itemCount || 0),
      total: (totalCents / 100).toFixed(2),
      hasCrossSells: Boolean(getCartCrossSellRoot()),
      hasHeavyBulky: stateEl.dataset.hasHeavyBulky === 'true',
      hasDropship: stateEl.dataset.hasDropship === 'true',
      pricingMix: stateEl.dataset.cartPricingMix || 'full_price'
    };
  }

  function setClarityCartStateTags() {
    const tags = getCartStateTags();
    if (!tags) return;

    if (window.aoClarity && typeof window.aoClarity.setCartState === 'function') {
      window.aoClarity.setCartState(tags);
      return;
    }

    if (typeof window.clarity !== 'function') return;

    window.clarity('set', 'cart_surface', tags.surface);
    window.clarity('set', 'cart_free_shipping_state', String(tags.freeShippingState));
    window.clarity('set', 'cart_value_band', String(tags.valueBand));
    window.clarity('set', 'cart_item_count', String(tags.itemCount));
    window.clarity('set', 'cart_total', String(tags.total));
    window.clarity('set', 'cart_has_cross_sells', String(tags.hasCrossSells));
    window.clarity('set', 'cart_has_heavy_bulky', String(tags.hasHeavyBulky));
    window.clarity('set', 'cart_has_dropship', String(tags.hasDropship));
    window.clarity('set', 'cart_pricing_mix', String(tags.pricingMix));
  }

  function isClarityTrackingReady() {
    return Boolean(window.aoClarity && typeof window.aoClarity.trackCartEvent === 'function') ||
      typeof window.clarityTrack === 'function';
  }

  function trackClarityCartEvent(action, detail = {}) {
    if (window.aoClarity && typeof window.aoClarity.trackCartEvent === 'function') {
      window.aoClarity.trackCartEvent(action, detail);
      return true;
    }

    if (typeof window.clarityTrack !== 'function') return false;

    if (action === 'open') {
      if (typeof window.clarity === 'function') {
        window.clarity('set', 'cart_surface', detail.surface || 'page');
        window.clarity('set', 'cart_open_type', detail.type || 'page');
        window.clarity('set', 'cart_open_source', detail.source || 'unknown');
      }

      window.clarityTrack('cart_open');
      return true;
    }

    if (action === 'close') {
      if (detail.source && typeof window.clarity === 'function') {
        window.clarity('set', 'cart_close_source', detail.source);
      }

      window.clarityTrack('cart_close');
      return true;
    }

    window.clarityTrack(action);
    return true;
  }

  function trackCartOpenWhenReady(detail, attempt = 0) {
    if (cartOpenTracked) return;

    if (isClarityTrackingReady()) {
      setClarityCartStateTags();
      cartOpenTracked = trackClarityCartEvent('open', detail);
      return;
    }

    if (attempt >= CLARITY_READY_MAX_ATTEMPTS) return;

    window.setTimeout(() => {
      trackCartOpenWhenReady(detail, attempt + 1);
    }, CLARITY_READY_RETRY_MS);
  }

  function trackClarityCartEventWhenReady(action, detail = {}, attempt = 0) {
    if (isClarityTrackingReady()) {
      return trackClarityCartEvent(action, detail);
    }

    if (attempt >= CLARITY_READY_MAX_ATTEMPTS) return false;

    window.setTimeout(() => {
      trackClarityCartEventWhenReady(action, detail, attempt + 1);
    }, CLARITY_READY_RETRY_MS);

    return false;
  }

  function trackCartStateChange() {
    const eventName = getCartFreeShippingEventName();
    if (!eventName) return;

    if (lastFreeShippingState === null) {
      lastFreeShippingState = eventName;
      return;
    }

    if (lastFreeShippingState === eventName) return;
    lastFreeShippingState = eventName;

    if (window.aoClarity && typeof window.aoClarity.trackCartDrawerState === 'function') {
      window.aoClarity.trackCartDrawerState(document);
    } else if (typeof window.clarityTrack === 'function') {
      window.clarityTrack(eventName);
    }
  }

  function getCartOpenSource() {
    try {
      const storedSource = window.sessionStorage.getItem('aoCartOpenSource');
      if (storedSource) {
        window.sessionStorage.removeItem('aoCartOpenSource');
        return storedSource;
      }
    } catch (error) {
      // Referrer fallback is enough when storage is unavailable.
    }

    if (!document.referrer) return 'direct';

    try {
      const referrerUrl = new URL(document.referrer);
      if (referrerUrl.origin !== window.location.origin) return 'external_referrer';
      if (referrerUrl.pathname.indexOf('/checkouts') !== -1 || referrerUrl.pathname.indexOf('/checkout') !== -1) {
        return 'checkout_return';
      }
    } catch (error) {
      return 'unknown';
    }

    return 'internal_link';
  }

  function getLineItemFromTarget(target) {
    if (!(target instanceof Element)) return null;

    return target.closest('.cart-page__item');
  }

  function getLineItemDetail(lineItem) {
    if (!lineItem) return {};

    return {
      line: Number(lineItem.dataset.line || 0),
      lineKey: lineItem.dataset.lineKey,
      productId: lineItem.dataset.productId,
      variantId: lineItem.dataset.variantId,
      sku: lineItem.dataset.sku,
      productTitle: lineItem.dataset.title,
      title: lineItem.dataset.title,
      vendor: lineItem.dataset.vendor,
      quantity: Number(lineItem.dataset.quantity || 0)
    };
  }

  function setPendingRemove(button) {
    const lineItem = getLineItemFromTarget(button);
    pendingCartAction = {
      action: 'remove',
      detail: Object.assign(getLineItemDetail(lineItem), {
        line: Number(button.dataset.line || lineItem?.dataset.line || 0),
        variantId: button.dataset.variantId || lineItem?.dataset.variantId,
        sku: button.dataset.sku || lineItem?.dataset.sku,
        productTitle: button.dataset.productTitle || lineItem?.dataset.title
      })
    };
  }

  function setPendingQuantityChange(select) {
    const lineItem = getLineItemFromTarget(select);
    const previousQuantity = Number(select.dataset.currentQuantity || lineItem?.dataset.quantity || 0);
    const quantity = Number(select.value || 0);
    const direction = quantity > previousQuantity ? 'increase' : quantity < previousQuantity ? 'decrease' : 'change';

    pendingCartAction = {
      action: quantity === 0 ? 'remove' : 'quantity',
      detail: Object.assign(getLineItemDetail(lineItem), {
        line: Number(select.dataset.line || lineItem?.dataset.line || 0),
        previousQuantity,
        quantity,
        nextQuantity: quantity,
        direction,
        variantId: select.dataset.variantId || lineItem?.dataset.variantId,
        sku: select.dataset.sku || lineItem?.dataset.sku,
        productTitle: select.dataset.productTitle || lineItem?.dataset.title
      })
    };
  }

  function trackPendingCartAction() {
    if (!pendingCartAction) return;

    trackClarityCartEvent(pendingCartAction.action, pendingCartAction.detail);
    pendingCartAction = null;
  }

  function replaceCartPageSection(html) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const next = getSectionRoot(doc);
    const current = getSectionRoot();

    if (!next || !current) return;

    if (current.classList.contains('cart-page')) {
      const nextCartPage = next.classList.contains('cart-page') ? next : next.querySelector('.cart-page');
      if (nextCartPage) current.outerHTML = nextCartPage.outerHTML;
      cartCrossSellGroups = null;
      initCrossSellNav();
      return;
    }

    current.innerHTML = next.classList.contains('cart-page') ? next.outerHTML : next.innerHTML;
    cartCrossSellGroups = null;
    initCrossSellNav();
  }

  function refreshCartPage(parsedState, line, quantity, cartPageSectionId) {
    const cartPageHtml = parsedState.sections && (
      parsedState.sections[cartPageSectionId] ||
      parsedState.sections[SECTION_TYPE]
    );

    if (cartPageHtml) replaceCartPageSection(cartPageHtml);

    if (parsedState.sections && parsedState.sections[CART_ICON_SECTION_ID]) {
      const cartIcon = document.getElementById(CART_ICON_SECTION_ID);
      if (cartIcon) cartIcon.innerHTML = getSectionInnerHTML(parsedState.sections[CART_ICON_SECTION_ID]);
    }

    updateCartIconCount(parsedState.item_count);
    setClarityCartStateTags();
    trackPendingCartAction();
    trackCartStateChange();

    document.dispatchEvent(new CustomEvent('cart:page-updated', {
      detail: {
        line: Number(line),
        quantity: Number(quantity),
        itemCount: parsedState.item_count
      }
    }));
  }

  function changeLine(line, quantity) {
    setLoading(true);
    const cartPageSectionId = getCartPageSectionId();

    return fetch(window.Shopify.routes.root + 'cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        line: Number(line),
        quantity: Number(quantity),
        sections: [cartPageSectionId, CART_ICON_SECTION_ID],
        sections_url: window.location.pathname
      })
    })
      .then((response) => {
        if (!response.ok) throw new Error('Cart update failed');
        return response.json();
      })
      .then((parsedState) => refreshCartPage(parsedState, line, quantity, cartPageSectionId))
      .finally(() => setLoading(false));
  }

  async function editOrderReference(itemKey, currentReference) {
    const input = prompt('Add, edit or remove your order reference(s):', currentReference);
    if (input === null) return;

    const newReference = input.trim();
    const cartPageSectionId = getCartPageSectionId();

    setLoading(true);

    try {
      const cart = await fetch(window.Shopify.routes.root + 'cart.js').then((response) => response.json());
      const lineItem = cart.items.find((item) => item.key === itemKey);
      if (!lineItem) return;

      const existingProperties = { ...(lineItem.properties || {}) };
      existingProperties['Order Reference(s)'] = newReference || '';

      const response = await fetch(window.Shopify.routes.root + 'cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: itemKey,
          quantity: lineItem.quantity,
          properties: existingProperties,
          sections: [cartPageSectionId, CART_ICON_SECTION_ID],
          sections_url: window.location.pathname
        })
      });

      if (!response.ok) {
        throw new Error(`Cart line item properties update failed. ${response.statusText} ${response.status}`);
      }

      const parsedState = await response.json();
      refreshCartPage(parsedState, 0, lineItem.quantity, cartPageSectionId);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  }

  function getCartCrossSellRoot() {
    return document.querySelector('[data-cart-cross-sell-root]');
  }

  function getCartCrossSellData() {
    if (cartCrossSellGroups) return cartCrossSellGroups;

    const dataEl = getCartCrossSellRoot()?.querySelector('[data-cart-cross-sell-data]');
    if (!dataEl) return null;

    try {
      cartCrossSellGroups = JSON.parse(dataEl.textContent || '[]');
    } catch (error) {
      console.warn('Cross-sell JSON parse failed', error);
      return null;
    }

    return cartCrossSellGroups;
  }

  function parseCrossSellBadges(raw) {
    if (!raw || typeof raw !== 'string') return [];

    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      return [];
    }
  }

  function isFinalSaleBadge(badges) {
    if (!Array.isArray(badges)) return false;

    return badges.some((badge) => String(badge && badge.text ? badge.text : '').toUpperCase().includes('FINAL SALE'));
  }

  function escapeHtml(value) {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function renderCrossSellBadges(badges) {
    if (!Array.isArray(badges) || !badges.length) return '';

    return badges
      .filter((badge) => badge && badge.text)
      .map((badge) => {
        const badgeClass = badge.class ? ` ${escapeHtml(badge.class)}` : '';
        return `<span class="badge${badgeClass}">${escapeHtml(badge.text)}</span>`;
      })
      .join('');
  }

  function getSelectedCrossSellOverlayVariant() {
    const overlay = document.querySelector('[data-cart-cross-sell-overlay]');
    if (!overlay || overlay.hidden) return {};

    const btn = overlay.querySelector('[data-overlay-add]');
    const openLink = overlay.querySelector('.cart-cross-sell-picker__open');

    return {
      parent: cartCrossSellOverlayState ? cartCrossSellOverlayState.parent : '',
      variantId: btn ? btn.dataset.variantId : '',
      url: openLink ? openLink.getAttribute('href') : ''
    };
  }

  function buildCartCrossSellUI(group) {
    const items = group && Array.isArray(group.items) ? group.items : [];
    if (!items.length) return '<p>No products available.</p>';

    const uniq = (arr) => [...new Set(arr)];
    const optionMap = (item) => {
      const map = {};
      (item.options || []).forEach((option) => {
        if (!option || !option.label) return;
        map[option.label] = option.value;
      });
      return map;
    };

    const prepared = items.map((item) => ({ ...item, optionMap: optionMap(item) }));
    const labels = uniq(prepared.flatMap((item) => Object.keys(item.optionMap)));
    const state = {};

    labels.forEach((label) => {
      state[label] = prepared[0].optionMap[label] || '';
    });

    const findMatch = () => {
      const exact = prepared.find((item) =>
        labels.every((label) => (item.optionMap[label] || '') === (state[label] || ''))
      );

      return exact || prepared[0];
    };

    const initial = findMatch();

    const html = `
      <div class="cart-cross-sell-picker">
        <div class="cart-cross-sell-picker__card">
          <div class="cart-cross-sell-picker__topbar">
            <h3 class="cart-cross-sell-picker__title">${escapeHtml((group && group.parent) || initial.title || '')}</h3>
            <a href="${escapeHtml(initial.url || '#')}" class="cart-cross-sell-picker__open" data-overlay-pdp-open aria-label="Open product page">
              <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
                <path fill="currentColor" d="M14 3h7v7h-2V6.41l-9.29 9.3-1.42-1.42 9.3-9.29H14V3z"></path>
                <path fill="currentColor" d="M5 5h7v2H7v10h10v-5h2v7H5V5z"></path>
              </svg>
            </a>
          </div>
          <div class="cart-cross-sell-picker__main">
            <div class="cart-cross-sell-picker__media">
              ${initial.image ? `<img class="cart-cross-sell-picker__image" src="${escapeHtml(initial.image)}" alt="${escapeHtml(initial.title || '')}" loading="lazy">` : ''}
            </div>
            <div class="cart-cross-sell-picker__details">
              <div class="cart-cross-sell-picker__filters" data-overlay-filters></div>
              <div class="cart-cross-sell-picker__price-row">
                <span class="cart-cross-sell-picker__compare" data-compare>${escapeHtml(initial.price_original || '')}</span>
                <span class="cart-cross-sell-picker__price" data-price>${escapeHtml(initial.price_final || '')}</span>
                <div class="cart-cross-sell-picker__badges" data-overlay-badges>${renderCrossSellBadges(initial.badges)}</div>
              </div>
              <div class="cart-cross-sell-picker__actions">
                <button type="button" class="cart-cross-sell-picker__add" data-overlay-add data-variant-id="${escapeHtml(initial.variant_id || '')}">
                  <span class="cross-sell-btn-label">Add To Cart</span>
                  <span class="cross-sell-btn-spinner" aria-hidden="true"></span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    `;

    setTimeout(() => {
      const root = document.querySelector('.cart-cross-sell-picker');
      if (!root) return;

      const filtersEl = root.querySelector('[data-overlay-filters]');
      const priceEl = root.querySelector('[data-price]');
      const compareEl = root.querySelector('[data-compare]');
      const btn = root.querySelector('[data-overlay-add]');
      const imageEl = root.querySelector('.cart-cross-sell-picker__image');
      const badgesEl = root.querySelector('[data-overlay-badges]');
      const openLinkEl = root.querySelector('.cart-cross-sell-picker__open');
      const rowSelects = new Map();
      const norm = (value) => (value || '').trim().toLowerCase();
      const priorityLabels = ['style', 'colour', 'color', 'size'];
      const orderedLabels = uniq(labels).filter(Boolean).sort((a, b) => {
        const pa = priorityLabels.indexOf(norm(a));
        const pb = priorityLabels.indexOf(norm(b));
        if (pa !== -1 && pb !== -1) return pa - pb;
        if (pa !== -1) return -1;
        if (pb !== -1) return 1;
        return norm(a).localeCompare(norm(b));
      });

      const filterItems = (criteria) =>
        prepared.filter((item) => Object.entries(criteria).every(([key, value]) => (item.optionMap[key] || '') === value));

      const allowedValuesFor = (label, criteria) =>
        uniq(filterItems(criteria).map((item) => item.optionMap[label]).filter(Boolean));

      const findExactMatch = () =>
        prepared.find((item) => orderedLabels.every((label) => (item.optionMap[label] || '') === (state[label] || '')));

      const resolveMatrix = () => {
        const resolved = { ...state };
        for (let i = 0; i < orderedLabels.length; i += 1) {
          const label = orderedLabels[i];
          const criteria = {};
          for (let j = 0; j < i; j += 1) criteria[orderedLabels[j]] = resolved[orderedLabels[j]];
          const allowed = allowedValuesFor(label, criteria);
          if (allowed.length && !allowed.includes(resolved[label])) resolved[label] = allowed[0];
        }
        orderedLabels.forEach((label) => {
          state[label] = resolved[label];
        });
      };

      const refreshAvailability = () => {
        for (let i = 0; i < orderedLabels.length; i += 1) {
          const label = orderedLabels[i];
          const select = rowSelects.get(label);
          if (!select) continue;
          const criteria = {};
          for (let j = 0; j < i; j += 1) criteria[orderedLabels[j]] = state[orderedLabels[j]];
          const allowed = allowedValuesFor(label, criteria);
          Array.from(select.options).forEach((option) => {
            option.disabled = !allowed.includes(option.value);
          });
          if (allowed.length && !allowed.includes(select.value)) {
            select.value = allowed[0];
            state[label] = allowed[0];
          }
        }
      };

      const applyMatch = (match) => {
        if (!match) return;
        if (priceEl) priceEl.textContent = match.price_final || '';
        if (compareEl) {
          compareEl.textContent = match.price_original || '';
          compareEl.style.display = match.price_original ? '' : 'none';
        }
        if (btn) {
          btn.dataset.variantId = match.variant_id || '';
          btn.dataset.crossSellBadges = JSON.stringify(Array.isArray(match.badges) ? match.badges : []);
        }
        if (imageEl) {
          imageEl.src = match.image || '';
          imageEl.alt = match.title || '';
        }
        if (openLinkEl) openLinkEl.href = match.url || '#';
        if (badgesEl) badgesEl.innerHTML = renderCrossSellBadges(match.badges);
      };

      if (openLinkEl && !openLinkEl.dataset.clarityBound) {
        openLinkEl.dataset.clarityBound = 'true';
        openLinkEl.addEventListener('click', () => {
          trackClarityCartEvent('cross_sell_pdp_open', getSelectedCrossSellOverlayVariant());
        });
      }

      filtersEl.innerHTML = '';
      orderedLabels.forEach((label) => {
        const values = uniq(prepared.map((item) => item.optionMap[label]).filter(Boolean));
        if (values.length <= 1) return;
        const field = document.createElement('div');
        const heading = document.createElement('div');
        const select = document.createElement('select');
        field.className = 'cart-cross-sell-picker__field';
        heading.className = 'cart-cross-sell-picker__field-label';
        heading.textContent = label;
        select.className = 'cart-cross-sell-picker__select';
        values.forEach((value) => {
          const option = document.createElement('option');
          option.value = value;
          option.textContent = value;
          select.appendChild(option);
        });
        select.value = state[label];
        select.addEventListener('change', () => {
          const prevState = { ...state };
          const previousMatch = findExactMatch() || prepared[0];
          state[label] = select.value;
          resolveMatrix();
          refreshAvailability();
          orderedLabels.forEach((orderedLabel) => {
            const currentSelect = rowSelects.get(orderedLabel);
            if (currentSelect && currentSelect.value !== state[orderedLabel]) currentSelect.value = state[orderedLabel];
          });

          const nextMatch = findExactMatch() || prepared[0];
          applyMatch(nextMatch);

          trackClarityCartEvent('cross_sell_variant_change', {
            parent: group && group.parent ? group.parent : '',
            optionName: label,
            previousValue: prevState[label] || '',
            value: state[label] || '',
            previousVariantId: previousMatch && previousMatch.variant_id,
            variantId: nextMatch && nextMatch.variant_id,
            productId: nextMatch && nextMatch.product_id,
            sku: nextMatch && nextMatch.sku
          });
        });
        field.appendChild(heading);
        field.appendChild(select);
        filtersEl.appendChild(field);
        rowSelects.set(label, select);
      });

      resolveMatrix();
      refreshAvailability();
      orderedLabels.forEach((label) => {
        const select = rowSelects.get(label);
        if (select && select.value !== state[label]) select.value = state[label];
      });
      applyMatch(findExactMatch() || prepared[0]);
    }, 0);

    return html;
  }

  function openCartCrossSellOverlay(group) {
    const root = getCartCrossSellRoot();
    const overlay = root && root.querySelector('[data-cart-cross-sell-overlay]');
    const content = root && root.querySelector('[data-cart-cross-sell-overlay-content]');
    if (!overlay || !content) return;

    content.innerHTML = buildCartCrossSellUI(group);
    cartCrossSellOverlayState = {
      parent: group && group.parent ? group.parent : '',
      addedToCart: false,
      closedTracked: false
    };
    overlay.hidden = false;
    lockCartPageScroll();
    requestAnimationFrame(() => updateCartCrossSellClosePosition(overlay));
  }

  function closeCartCrossSellOverlay() {
    const root = getCartCrossSellRoot();
    const overlay = root && root.querySelector('[data-cart-cross-sell-overlay]');
    const content = root && root.querySelector('[data-cart-cross-sell-overlay-content]');
    if (!overlay || !content || overlay.hidden) return;

    if (
      cartCrossSellOverlayState &&
      !cartCrossSellOverlayState.addedToCart &&
      !cartCrossSellOverlayState.closedTracked
    ) {
      trackClarityCartEvent('cross_sell_close_without_atc', getSelectedCrossSellOverlayVariant());
      cartCrossSellOverlayState.closedTracked = true;
    }

    overlay.hidden = true;
    overlay.style.removeProperty('--cart-cross-sell-close-bottom');
    content.innerHTML = '';
    cartCrossSellOverlayState = null;
    unlockCartPageScroll();
  }

  function updateCartCrossSellClosePosition(overlay = document.querySelector('[data-cart-cross-sell-overlay]')) {
    if (!overlay || overlay.hidden || window.matchMedia('(min-width: 990px)').matches) return;

    const dialog = overlay.querySelector('.cart-cross-sell-overlay__dialog');
    const closeButton = overlay.querySelector('.cart-cross-sell-overlay__floating-close');
    if (!dialog || !closeButton) return;

    const dialogTop = dialog.getBoundingClientRect().top;
    const closeHeight = closeButton.offsetHeight || 56;
    const closeGap = 8;
    const closeBottom = Math.max(16, window.innerHeight - dialogTop + closeGap);

    overlay.style.setProperty('--cart-cross-sell-close-bottom', `${Math.round(closeBottom)}px`);
  }

  function lockCartPageScroll() {
    if (!cartCrossSellScrollLocked) {
      cartCrossSellScrollY = window.scrollY || window.pageYOffset || 0;
      cartCrossSellScrollLocked = true;

      if (window.matchMedia('(max-width: 749px)').matches) {
        document.body.style.top = `-${cartCrossSellScrollY}px`;
        document.body.style.left = '0';
        document.body.style.width = '100%';
      }
    }

    document.body.classList.add('no-scroll');
    document.documentElement.classList.add('cart-overlay-active');
  }

  function unlockCartPageScroll() {
    const shouldRestoreScroll = cartCrossSellScrollLocked;
    const scrollY = cartCrossSellScrollY;

    document.body.classList.remove('no-scroll');
    document.documentElement.classList.remove('cart-overlay-active');
    document.body.style.top = '';
    document.body.style.left = '';
    document.body.style.width = '';
    cartCrossSellScrollLocked = false;
    cartCrossSellScrollY = 0;

    if (shouldRestoreScroll) window.scrollTo(0, scrollY);
  }

  async function addCrossSellVariant(variantId, position, meta = {}) {
    const cartPageSectionId = getCartPageSectionId();
    const properties = {
      _cart_upsell: `position:${position}`
    };

    if (isFinalSaleBadge(meta.badges)) properties['Final Sale'] = 'No Returns*';

    const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({
        items: [
          {
            id: Number(variantId),
            quantity: 1,
            properties
          }
        ],
        sections: [cartPageSectionId, CART_ICON_SECTION_ID],
        sections_url: window.location.pathname
      })
    });

    if (!response.ok) throw new Error('Cross-sell add failed');

    const parsedState = await response.json();
    refreshCartPage(parsedState, 0, 1, cartPageSectionId);
  }

  function updateCrossSellNavState(wrapper) {
    const track = wrapper.querySelector('[data-cross-sell-track]');
    const leftBtn = wrapper.querySelector('[data-cross-sell-nav="left"]');
    const rightBtn = wrapper.querySelector('[data-cross-sell-nav="right"]');
    const leftFade = wrapper.querySelector('.cross-sell-fade--left');
    const rightFade = wrapper.querySelector('.cross-sell-fade--right');
    if (!track || !leftBtn || !rightBtn) return;

    const scrollLeft = track.scrollLeft;
    const maxScroll = track.scrollWidth - track.clientWidth;
    const atStart = scrollLeft <= 5;
    const atEnd = scrollLeft >= maxScroll - 5;
    const hasOverflow = track.scrollWidth > track.clientWidth;

    leftBtn.classList.toggle('is-disabled', atStart);
    rightBtn.classList.toggle('is-disabled', atEnd);
    if (leftFade) leftFade.classList.toggle('is-visible', hasOverflow && !atStart);
    if (rightFade) rightFade.classList.toggle('is-visible', hasOverflow && !atEnd);
    leftBtn.style.display = hasOverflow ? '' : 'none';
    rightBtn.style.display = hasOverflow ? '' : 'none';
  }

  function placeCrossSells() {
    const root = getCartCrossSellRoot();
    if (!root) return;

    const isDesktop = window.matchMedia('(min-width: 990px)').matches;
    const items = document.querySelector('.cart-page__items');
    const summary = document.querySelector('.cart-page__summary');

    if (isDesktop && summary) {
      const paymentMethods = summary.querySelector('.cart-page__payment-methods');
      if (paymentMethods && paymentMethods.nextElementSibling !== root) {
        paymentMethods.insertAdjacentElement('afterend', root);
      } else if (!paymentMethods && root.parentElement !== summary) {
        summary.appendChild(root);
      }
      return;
    }

    if (items && root.parentElement !== items) items.appendChild(root);
  }

  function initCrossSellNav() {
    placeCrossSells();
    document.querySelectorAll('.cart-page__cross-sells-wrapper').forEach((wrapper) => updateCrossSellNavState(wrapper));
    trackCartCrossSellImpression();
    observeCartCrossSellView();
  }

  function trackCartCrossSellImpression() {
    if (crossSellImpressedThisPage) return;
    if (!getCartCrossSellRoot()) return;

    crossSellImpressedThisPage = true;
    trackClarityCartEventWhenReady('cross_sell_impression');
  }

  function disconnectCrossSellViewObserver() {
    clearCrossSellViewTimer();
    if (!crossSellViewObserver) return;

    crossSellViewObserver.disconnect();
    crossSellViewObserver = null;
  }

  function clearCrossSellViewTimer() {
    if (!crossSellViewTimer) return;

    clearTimeout(crossSellViewTimer);
    crossSellViewTimer = null;
  }

  function startCrossSellViewTimer() {
    if (crossSellViewTimer || crossSellViewedThisPage) return;

    crossSellViewTimer = setTimeout(() => {
      crossSellViewTimer = null;
      if (crossSellViewedThisPage) return;

      crossSellViewedThisPage = true;
      trackClarityCartEvent('cross_sell_view');
      disconnectCrossSellViewObserver();
    }, CROSS_SELL_VIEW_DWELL_MS);
  }

  function observeCartCrossSellView() {
    disconnectCrossSellViewObserver();
    if (crossSellViewedThisPage) return;

    const crossSellRoot = getCartCrossSellRoot();
    if (!crossSellRoot) return;

    if (typeof IntersectionObserver !== 'function') {
      crossSellViewedThisPage = true;
      trackClarityCartEvent('cross_sell_view');
      return;
    }

    crossSellViewObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (crossSellViewedThisPage) return;

        if (entry.isIntersecting && entry.intersectionRatio >= CROSS_SELL_VIEW_THRESHOLD) {
          startCrossSellViewTimer();
        } else {
          clearCrossSellViewTimer();
        }
      });
    }, {
      root: null,
      threshold: [0, CROSS_SELL_VIEW_THRESHOLD, 1]
    });

    crossSellViewObserver.observe(crossSellRoot);
  }

  document.addEventListener('click', function (event) {
    const button = event.target.closest('.cart-page__remove');
    if (!button) return;

    event.preventDefault();
    button.classList.add('cart-page__remove--loading');
    setPendingRemove(button);
    changeLine(button.dataset.line, 0);
  });

  document.addEventListener('change', function (event) {
    const select = event.target.closest('.cart-page__quantity-select');
    if (!select) return;

    setPendingQuantityChange(select);
    changeLine(select.dataset.line, select.value);
  });

  document.addEventListener('click', function (event) {
    const link = event.target.closest('.edit-order-reference');
    if (!link || !getCartPage()?.contains(link)) return;

    event.preventDefault();
    editOrderReference(link.getAttribute('data-order-ref-key'), link.getAttribute('data-order-ref-value') || '');
  });

  document.addEventListener('click', function (event) {
    const nav = event.target.closest('[data-cross-sell-nav]');
    if (!nav) return;

    const wrapper = nav.closest('.cart-page__cross-sells-wrapper');
    const track = wrapper && wrapper.querySelector('[data-cross-sell-track]');
    if (!track) return;

    event.preventDefault();
    const direction = nav.dataset.crossSellNav === 'left' ? 'left' : 'right';
    track.scrollBy({ left: (direction === 'left' ? -1 : 1) * Math.max(track.clientWidth * 0.85, 220), behavior: 'smooth' });
    setTimeout(() => updateCrossSellNavState(wrapper), 350);
    trackClarityCartEvent('cross_sell_nav', { direction });
  });

  document.addEventListener('scroll', function (event) {
    const track = event.target.closest && event.target.closest('[data-cross-sell-track]');
    if (!track) return;

    const wrapper = track.closest('.cart-page__cross-sells-wrapper');
    if (wrapper) updateCrossSellNavState(wrapper);
  }, true);

  document.addEventListener('click', function (event) {
    const trigger = event.target.closest('[data-cross-sell-open]');
    if (!trigger) return;

    const groups = getCartCrossSellData();
    if (!Array.isArray(groups) || !groups.length) return;

    const parent = trigger.dataset.crossSellParent || trigger.closest('[data-cross-sell-parent]')?.dataset.crossSellParent || '';
    const group = groups.find((item) => item && item.parent === parent) || groups[0];
    if (!group) return;

    event.preventDefault();
    trackClarityCartEvent('cross_sell_open', {
      parent,
      variantId: trigger.dataset.variantId,
      productId: trigger.dataset.productId,
      sku: trigger.dataset.sku
    });
    openCartCrossSellOverlay(group);
  });

  document.addEventListener('click', function (event) {
    if (!event.target.closest('[data-cart-cross-sell-close]')) return;

    event.preventDefault();
    closeCartCrossSellOverlay();
  });

  document.addEventListener('click', async function (event) {
    const button = event.target.closest('[data-overlay-add]');
    if (!button) return;

    event.preventDefault();
    if (button.classList.contains('is-loading')) return;

    const selected = getSelectedCrossSellOverlayVariant();
    const variantId = selected.variantId || button.dataset.variantId;
    if (!variantId) return;

    const meta = {
      parent: selected.parent,
      badges: parseCrossSellBadges(button.dataset.crossSellBadges)
    };

    button.classList.add('is-loading');

    try {
      await addCrossSellVariant(variantId, selected.parent || 'cart-page', meta);
      if (cartCrossSellOverlayState) cartCrossSellOverlayState.addedToCart = true;
      trackClarityCartEvent('cross_sell_atc', {
        variantId,
        parent: selected.parent,
        position: 'overlay'
      });
      unlockCartPageScroll();
      cartCrossSellOverlayState = null;
    } catch (error) {
      console.error(error);
      button.classList.remove('is-loading');
    }
  });

  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Escape') return;
    closeCartCrossSellOverlay();
  });

  document.addEventListener('click', function (event) {
    const checkoutButton = event.target.closest('#checkout, .cart__checkout-button, [name="checkout"]');
    if (!checkoutButton || !getCartPage()?.contains(checkoutButton)) return;

    setClarityCartStateTags();
    trackClarityCartEvent('checkout', { surface: 'page' });
  });

  window.addEventListener('resize', function () {
    initCrossSellNav();
    updateCartCrossSellClosePosition();
  });

  function initCartPage() {
    if (!getCartPage()) return;

    trackCartOpenWhenReady({
      surface: 'page',
      type: 'page',
      source: getCartOpenSource()
    });
    setClarityCartStateTags();
    trackCartStateChange();
    initCrossSellNav();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCartPage);
  } else {
    initCartPage();
  }
})();
